import { asIsoDate, type IsoDate } from '@stockstop/shared';
import type { IChartApi, ISeriesApi, Logical, Time } from 'lightweight-charts';

/**
 * Translation between data space (trading day + price) and screen pixels.
 *
 * Drawings are stored in data space so they survive zoom, pan and resize. Every
 * render converts them to pixels; every drag converts back. Both directions live
 * here so the two can never disagree.
 *
 * The x-axis deliberately goes through *logical indices* rather than
 * `timeToCoordinate`. `timeToCoordinate` only resolves timestamps that exist in
 * the series, which would break the moment an anchor lands on a weekend, a market
 * holiday or a gap. A logical index is the bar's position in the series, and the
 * scale maps it linearly, so fractional and out-of-range values still produce a
 * sensible coordinate.
 */
export class ChartCoordinates {
  /** Trading day -> position in the series. */
  private readonly indexByDate = new Map<string, number>();
  /** Position -> trading day, for the reverse lookup while dragging. */
  private dates: IsoDate[] = [];

  constructor(
    private readonly chart: IChartApi,
    private readonly series: ISeriesApi<'Candlestick'>,
  ) {}

  /** Rebuilds the index after the series data changed. */
  setDates(dates: readonly IsoDate[]): void {
    this.dates = [...dates];
    this.indexByDate.clear();
    dates.forEach((date, index) => this.indexByDate.set(date, index));
  }

  get barCount(): number {
    return this.dates.length;
  }

  get lastDate(): IsoDate | null {
    return this.dates.at(-1) ?? null;
  }

  /**
   * Logical index for a trading day.
   *
   * Dates between two bars are interpolated, dates beyond either end are
   * extrapolated using the average calendar days per bar. That keeps an anchor
   * placed on a non-trading day exactly where the user put it.
   */
  indexForDate(date: IsoDate): number {
    const exact = this.indexByDate.get(date);
    if (exact !== undefined) return exact;
    if (this.dates.length === 0) return 0;

    const first = this.dates[0]!;
    const last = this.dates.at(-1)!;
    const dayMs = 86_400_000;
    const toTime = (value: string): number => Date.parse(`${value}T00:00:00Z`);

    /* Average calendar days per bar — roughly 1.4 with weekends excluded. */
    const span = (toTime(last) - toTime(first)) / dayMs;
    const perBar = this.dates.length > 1 ? span / (this.dates.length - 1) : 1;

    if (date < first) return -((toTime(first) - toTime(date)) / dayMs) / perBar;
    if (date > last) return this.dates.length - 1 + (toTime(date) - toTime(last)) / dayMs / perBar;

    /* Inside the range but not a trading day: interpolate between neighbours. */
    let low = 0;
    let high = this.dates.length - 1;
    while (low < high - 1) {
      const mid = Math.floor((low + high) / 2);
      if (this.dates[mid]! < date) low = mid;
      else high = mid;
    }
    const lowTime = toTime(this.dates[low]!);
    const highTime = toTime(this.dates[high]!);
    const ratio = highTime === lowTime ? 0 : (toTime(date) - lowTime) / (highTime - lowTime);
    return low + ratio;
  }

  /** Trading day for a logical index, clamped to the available series. */
  dateForIndex(index: number): IsoDate | null {
    if (this.dates.length === 0) return null;
    const clamped = Math.max(0, Math.min(this.dates.length - 1, Math.round(index)));
    return this.dates[clamped] ?? null;
  }

  /**
   * Trading day -> x pixel, or null while the chart has no layout yet.
   *
   * `Logical` is a branded number in lightweight-charts; the cast states that our
   * computed index is one, which the library cannot infer from a plain number.
   */
  xForDate(date: IsoDate): number | null {
    return this.chart.timeScale().logicalToCoordinate(this.indexForDate(date) as Logical);
  }

  /** x pixel -> nearest trading day. */
  dateForX(x: number): IsoDate | null {
    const logical = this.chart.timeScale().coordinateToLogical(x);
    return logical === null ? null : this.dateForIndex(logical);
  }

  /** Price -> y pixel. */
  yForPrice(price: number): number | null {
    return this.series.priceToCoordinate(price);
  }

  /** y pixel -> price. */
  priceForY(y: number): number | null {
    const price = this.series.coordinateToPrice(y);
    return price;
  }

  /** Time value the series uses, for API calls that expect one. */
  timeForDate(date: IsoDate): Time {
    return date;
  }

  /** Converts a chart time back into our date type. */
  dateFromTime(time: Time): IsoDate {
    if (typeof time === 'string') return asIsoDate(time);
    if (typeof time === 'number')
      return asIsoDate(new Date(time * 1000).toISOString().slice(0, 10));
    const { year, month, day } = time;
    const pad = (value: number): string => String(value).padStart(2, '0');
    return asIsoDate(`${year}-${pad(month)}-${pad(day)}`);
  }
}
